import React, { useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  TextField,
  Autocomplete,
} from '@mui/material';
import DryCleaningIcon from '@mui/icons-material/DryCleaning';
import { environmentSafeLog } from '../../utils/environment';

interface LooksInformationProps {
  asset: any;
  isEditable?: boolean;
  onMetadataChange?: (updatedMetadata: any) => void;
}

const LooksInformation: React.FC<LooksInformationProps> = ({
  asset,
  isEditable = false,
  onMetadataChange,
}) => {
  // ENHANCED: Priority-based metadata extraction with backend compatibility (following StarInformation pattern)
  const looksMetadata = React.useMemo(() => {
    console.log('🚨 [LOOKS INFO DEBUG] Full asset object received:', asset);
    console.log(
      '🚨 [LOOKS INFO DEBUG] asset.looksMetadata:',
      asset.looksMetadata
    );

    environmentSafeLog('🔎 [LOOKS INFO] Extracting looksMetadata from asset:', {
      hasLooksMetadata: !!asset.looksMetadata,
      hasAiLooksMetadata: !!asset.aiMetadata?.looksMetadata,
      hasMetadataLooksMetadata: !!asset.metadata?.looksMetadata,
      hasRootLevelFields: !!(asset.brandNames || asset.styleCategory || asset.primaryColors),
    });

    let rawMetadata = null;

    // Priority 1: Root level looksMetadata (AI-generated)
    if (asset.looksMetadata) {
      environmentSafeLog('📌 [LOOKS INFO] Using root looksMetadata');
      rawMetadata = asset.looksMetadata;
    }
    // Priority 2: AI metadata structure (nested)
    else if (asset.aiMetadata?.looksMetadata) {
      environmentSafeLog('📌 [LOOKS INFO] Using aiMetadata.looksMetadata');
      rawMetadata = asset.aiMetadata.looksMetadata;
    }
    // Priority 3: Legacy metadata structure
    else if (asset.metadata?.looksMetadata) {
      environmentSafeLog('📌 [LOOKS INFO] Using metadata.looksMetadata');
      rawMetadata = asset.metadata.looksMetadata;
    }
    // Priority 4: ROOT LEVEL FIELDS - Backend stores Looks metadata at root level
    else if (asset.brandNames || asset.styleCategory || asset.primaryColors || asset.primaryGarment) {
      environmentSafeLog('📌 [LOOKS INFO] Using ROOT level fields (backend storage format)');
        rawMetadata = {
        brandNames: asset.brandNames,
        styleCategory: asset.styleCategory,
        primaryColors: asset.primaryColors,
        colorScheme: asset.colorScheme,
        primaryGarment: asset.primaryGarment,
        seasonality: asset.seasonality,
        occasion: asset.occasion,
        accessories: asset.accessories,
        patterns: asset.patterns,
        materials: asset.materials,
        priceRange: asset.priceRange,
        formality: asset.formality,
        outfitName: asset.outfitName,
        timeOfDay: asset.timeOfDay,
        energyLevel: asset.energyLevel,
      };
    } else {
      environmentSafeLog(
        '⚠️ [LOOKS INFO] No looks metadata found, using empty object'
      );
      rawMetadata = {};
    }

    // CRITICAL FIX: Apply breakthrough pattern for nested data access
    const actualLooksMetadata =
      rawMetadata?.looksMetadata ||
      rawMetadata?.data?.looksMetadata ||
      rawMetadata;
    environmentSafeLog(
      '🔄 [LOOKS INFO] Actual metadata after nested access:',
      actualLooksMetadata
    );

    console.log(
      '🚨 [LOOKS INFO DEBUG] Final extracted looksMetadata:',
      actualLooksMetadata
    );
    console.log(
      '🚨 [LOOKS INFO DEBUG] Has primaryColors:',
      actualLooksMetadata?.primaryColors
    );
    console.log(
      '🚨 [LOOKS INFO DEBUG] Has brandNames:',
      actualLooksMetadata?.brandNames
    );

    return actualLooksMetadata;
  }, [asset]);

  // State for editable fields - initialize with extracted metadata
  const [editableData, setEditableData] = React.useState(() => {
    console.log(
      '🚀 [LOOKS INFO INIT] Initializing editableData state with looksMetadata:',
      looksMetadata
    );
    // 🔧 FIELD MAPPING FIX: Handle backend array fields properly
    const primaryColorValue = Array.isArray(looksMetadata?.primaryColors)
      ? looksMetadata.primaryColors[0] || ''
      : '';

    const brandNamesValue = Array.isArray(looksMetadata?.brandNames)
      ? looksMetadata.brandNames[0] || ''
      : looksMetadata?.brandNames || looksMetadata?.brandName || '';

    const colorSchemeStr = Array.isArray(looksMetadata?.colorScheme)
      ? (looksMetadata.colorScheme[0] ?? looksMetadata.colorScheme.join(', ') ?? '')
      : (typeof looksMetadata?.colorScheme === 'string' ? looksMetadata.colorScheme : '');
    const occasionStr = Array.isArray(looksMetadata?.occasion)
      ? (looksMetadata.occasion[0] ?? looksMetadata.occasion.join(', ') ?? '')
      : (typeof looksMetadata?.occasion === 'string' ? looksMetadata.occasion : '');

    return {
      brandNames: brandNamesValue,
      styleCategory: looksMetadata?.styleCategory || '',
      primaryColor: primaryColorValue,
      colorScheme: colorSchemeStr,
      primaryGarment: looksMetadata?.primaryGarment || '',
      seasonality: looksMetadata?.seasonality || '',
      occasion: occasionStr,
      accessories: Array.isArray(looksMetadata?.accessories)
        ? looksMetadata.accessories.join(', ')
        : looksMetadata?.accessories || '',
    };
  });

  // 🚀 PERFORMANCE OPTIMIZATION: Only update when AI metadata actually changes (prevent loops)
  const previousMetadataRef = useRef<string>('');

  React.useEffect(() => {
    // Create a stable hash of only the relevant AI metadata fields
    const metadataHash = JSON.stringify({
      brandNames: looksMetadata?.brandNames,
      styleCategory: looksMetadata?.styleCategory,
      primaryColors: looksMetadata?.primaryColors,
      colorScheme: looksMetadata?.colorScheme,
      primaryGarment: looksMetadata?.primaryGarment,
      seasonality: looksMetadata?.seasonality,
      occasion: looksMetadata?.occasion,
      accessories: looksMetadata?.accessories,
    });

    // Only update if the metadata actually changed (prevents user typing feedback loop)
    if (looksMetadata && metadataHash !== previousMetadataRef.current) {
      console.log(
        '🔄 [LOOKS INFO] AI metadata changed - updating editableData once'
      );

      // 🔧 FIELD MAPPING FIX: Handle backend array fields properly in useEffect
      const primaryColorValue = Array.isArray(looksMetadata?.primaryColors)
        ? looksMetadata.primaryColors[0] || ''
        : looksMetadata?.primaryColor || '';

      const brandNamesValue = Array.isArray(looksMetadata?.brandNames)
        ? looksMetadata.brandNames[0] || ''
        : looksMetadata?.brandNames || looksMetadata?.brandName || '';

      const colorSchemeStr = Array.isArray(looksMetadata?.colorScheme)
        ? (looksMetadata.colorScheme[0] ?? looksMetadata.colorScheme.join(', ') ?? '')
        : (typeof looksMetadata?.colorScheme === 'string' ? looksMetadata.colorScheme : '');
      const occasionStr = Array.isArray(looksMetadata?.occasion)
        ? (looksMetadata.occasion[0] ?? looksMetadata.occasion.join(', ') ?? '')
        : (typeof looksMetadata?.occasion === 'string' ? looksMetadata.occasion : '');

      const newEditableData = {
        brandNames: brandNamesValue,
        styleCategory: looksMetadata?.styleCategory || '',
        primaryColor: primaryColorValue,
        colorScheme: colorSchemeStr,
        primaryGarment: looksMetadata?.primaryGarment || '',
        seasonality: looksMetadata?.seasonality || '',
        occasion: occasionStr,
        accessories: Array.isArray(looksMetadata?.accessories)
          ? looksMetadata.accessories.join(', ')
          : looksMetadata?.accessories || '',
      };

      console.log(
        '✅ [LOOKS INFO] Setting new editableData from AI:',
        newEditableData
      );
      setEditableData(newEditableData);
      previousMetadataRef.current = metadataHash;
    }
  }, [looksMetadata]);

  // 🚀 PERFORMANCE FIX: Debounced field change handler to prevent excessive re-renders
  const debounceTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});

  const handleFieldChange = useCallback(
    (field: string, value: string) => {
      // Immediately update local state for responsive UI
      setEditableData(prev => ({ ...prev, [field]: value }));

      // Debounce the parent callback to prevent excessive re-renders
      if (debounceTimeouts.current[field]) {
        clearTimeout(debounceTimeouts.current[field]);
      }

      debounceTimeouts.current[field] = setTimeout(() => {
        if (onMetadataChange) {
          setEditableData(current => {
            // Build payload: API uses primaryColors array, not primaryColor
            const processedData = { ...current } as any;
            if (
              field === 'accessories' &&
              typeof processedData.accessories === 'string'
            ) {
              processedData.accessories = processedData.accessories
                .split(',')
                .map((item: string) => item.trim())
                .filter((item: string) => item.length > 0);
            }
            if (field === 'primaryColor') {
              processedData.primaryColors = processedData.primaryColor
                ? [processedData.primaryColor]
                : [];
              delete processedData.primaryColor;
            }
            onMetadataChange(processedData);
            return current;
          });
        }
      }, 500); // 500ms debounce (increased for smoother typing)
    },
    [onMetadataChange]
  );

  // Cleanup debounce timeouts on unmount
  React.useEffect(() => {
    return () => {
      Object.values(debounceTimeouts.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  // 🚀 PERFORMANCE OPTIMIZATION: Memoized dropdown options to prevent excessive recalculation
  const basePrimaryColors = useMemo(
    () => [
      'black',
      'white',
      'gray',
      'red',
      'blue',
      'navy',
      'green',
      'yellow',
      'orange',
      'purple',
      'pink',
      'brown',
      'beige',
      'tan',
      'gold',
      'silver',
    ],
    []
  );

  const baseBrandNames = useMemo(
    () => [
      'nike',
      'adidas',
      'zara',
      'h&m',
      'uniqlo',
      'gap',
      'old navy',
      'target',
      'walmart',
      'amazon',
      'shein',
      'forever 21',
      'urban outfitters',
      'anthropologie',
      'j.crew',
      'banana republic',
      'ann taylor',
      'loft',
      'express',
      'the limited',
      'louis vuitton',
      'gucci',
      'prada',
      'chanel',
      'dior',
      'versace',
      'armani',
      'calvin klein',
      'tommy hilfiger',
      'ralph lauren',
      'lacoste',
      'hugo boss',
    ],
    []
  );

  // Create dynamic dropdown that includes AI-generated value + base options
  const createDynamicOptions = useCallback(
    (aiValue: string | string[] | undefined, baseOptions: string[]) => {
      // Handle different input types safely
      let stringValue: string;

      if (Array.isArray(aiValue)) {
        // If aiValue is an array, join it or use the first element
        stringValue = aiValue.length > 0 ? aiValue[0] : '';
      } else if (typeof aiValue === 'string') {
        stringValue = aiValue;
      } else {
        stringValue = '';
      }

      if (!stringValue || !stringValue.trim()) return baseOptions;

      const normalizedAiValue = stringValue.toLowerCase().trim();
      const normalizedOptions = baseOptions.map(opt => opt.toLowerCase());

      // If AI value is not in base options, add it at the top
      if (!normalizedOptions.includes(normalizedAiValue)) {
        return [stringValue.trim(), ...baseOptions];
      }

      return baseOptions;
    },
    []
  );

  // 🚀 MEMOIZED OPTIONS: Only recalculate when actual AI values change
  const brandNames = useMemo(() => {
    // Handle brandNames array field properly
    const brandNamesValue = Array.isArray(looksMetadata?.brandNames)
      ? looksMetadata.brandNames[0] || ''
      : looksMetadata?.brandNames || '';

    return createDynamicOptions(brandNamesValue, baseBrandNames);
  }, [looksMetadata?.brandNames, baseBrandNames, createDynamicOptions]);

  const styleCategories = useMemo(
    () =>
      createDynamicOptions(looksMetadata?.styleCategory || '', [
        'streetwear',
        'casual',
        'formal',
        'athletic',
        'vintage',
        'trendy',
        'contemporary',
        'classic',
        'modern',
        'bohemian',
        'minimalist',
        'edgy',
        'romantic',
        'preppy',
      ]),
    [looksMetadata?.styleCategory, createDynamicOptions]
  );

  const primaryColors = useMemo(
    () =>
      createDynamicOptions(
        looksMetadata?.primaryColors, // Use plural array field from backend
        basePrimaryColors
      ),
    [looksMetadata?.primaryColors, basePrimaryColors, createDynamicOptions]
  );

  const colorSchemeForOptions = typeof looksMetadata?.colorScheme === 'string'
    ? looksMetadata.colorScheme
    : Array.isArray(looksMetadata?.colorScheme)
      ? (looksMetadata.colorScheme[0] ?? looksMetadata.colorScheme.join(', ') ?? '')
      : '';
  const colorSchemes = useMemo(
    () =>
      createDynamicOptions(colorSchemeForOptions, [
        'monochromatic',
        'complementary',
        'triadic',
        'analogous',
        'split-complementary',
        'tetradic',
        'neutral',
        'warm',
        'cool',
        'pastel',
        'bold',
        'muted',
      ]),
    [colorSchemeForOptions, createDynamicOptions]
  );

  const primaryGarments = useMemo(
    () =>
      createDynamicOptions(looksMetadata?.primaryGarment || '', [
        'dress',
        'top',
        'shirt',
        'blouse',
        'sweater',
        'jacket',
        'coat',
        'pants',
        'jeans',
        'skirt',
        'shorts',
        'jumpsuit',
        'romper',
        'suit',
      ]),
    [looksMetadata?.primaryGarment, createDynamicOptions]
  );

  const seasonalities = useMemo(
    () =>
      createDynamicOptions(looksMetadata?.seasonality || '', [
        'spring',
        'summer',
        'fall',
        'winter',
        'year-round',
        'transitional',
      ]),
    [looksMetadata?.seasonality, createDynamicOptions]
  );

  const occasionForOptions = typeof looksMetadata?.occasion === 'string'
    ? looksMetadata.occasion
    : Array.isArray(looksMetadata?.occasion)
      ? (looksMetadata.occasion[0] ?? looksMetadata.occasion.join(', ') ?? '')
      : '';
  const occasions = useMemo(
    () =>
      createDynamicOptions(occasionForOptions, [
        'casual',
        'work',
        'formal',
        'party',
        'date',
        'travel',
        'athletic',
        'lounge',
        'business',
        'cocktail',
        'wedding',
        'vacation',
        'everyday',
        'special-occasion',
        'night-out',
        'brunch',
      ]),
    [occasionForOptions, createDynamicOptions]
  );

  if (!looksMetadata || Object.keys(looksMetadata).length === 0) {
    return null;
  }

  return (
    <>
      <Divider sx={{ mt: 3, mb: 3 }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <DryCleaningIcon />
          👕 Looks Metadata
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {/* 
        REMOVED: Asset Type & Base Look ID display 
        REASON: Causes layout shifts during editing - will be added back in Phase 3
        TODO: Re-implement in Phase 3 when base/variant system is fully designed
        */}

        {/* Brand Names - 🚀 HYBRID AUTOCOMPLETE WITH FREE TEXT */}
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Brand Names
          </Typography>
          {isEditable ? (
            <Autocomplete
              key={`brandNames-${looksMetadata?.brandNames || 'default'}`} // STABLE KEY: Only changes with AI data
              freeSolo
              size="small"
              value={editableData.brandNames}
              inputValue={editableData.brandNames}
              onChange={(event, newValue) => {
                handleFieldChange('brandNames', newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                handleFieldChange('brandNames', newInputValue);
              }}
              options={brandNames}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Brand Names"
                  placeholder="Type or select brands (e.g., Nike, Louis Vuitton)"
                  helperText="Select from list or type your own brand"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    {/* Show AI chip for AI-generated values */}
                    {option === looksMetadata?.brandNames && (
                      <Chip
                        size="small"
                        label="AI"
                        sx={{
                          height: 16,
                          fontSize: '0.7rem',
                          bgcolor: 'success.light',
                          color: 'success.contrastText',
                        }}
                      />
                    )}
                    {option.charAt(0).toUpperCase() +
                      option
                        .slice(1)
                        .replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </Box>
                </li>
              )}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {Array.isArray(looksMetadata.brandNames)
                ? looksMetadata.brandNames.join(', ')
                : looksMetadata.brandNames ||
                  looksMetadata.brandName ||
                  'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* Style Category - 🚀 CONVERTED TO AUTOCOMPLETE FOR AUTO-POPULATION */}
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Style Category
          </Typography>
          {isEditable ? (
            <Autocomplete
              key={`styleCategory-${looksMetadata?.styleCategory || 'default'}`} // STABLE KEY: Only changes with AI data
              freeSolo
              size="small"
              value={editableData.styleCategory}
              inputValue={editableData.styleCategory} // CRITICAL FIX: Explicit inputValue binding
              onChange={(event, newValue) => {
                handleFieldChange('styleCategory', newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                handleFieldChange('styleCategory', newInputValue);
              }}
              options={styleCategories}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Style Category"
                  placeholder="Type or select style category"
                  helperText="Select from list or type your own style"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    {/* Show AI chip for AI-generated values */}
                    {option.toLowerCase() ===
                      looksMetadata?.styleCategory?.toLowerCase() && (
                      <Chip
                        size="small"
                        label="AI"
                        sx={{
                          height: 16,
                          fontSize: '0.7rem',
                          bgcolor: 'success.light',
                          color: 'success.contrastText',
                        }}
                      />
                    )}
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </Box>
                </li>
              )}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {looksMetadata.styleCategory || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* Primary Color - 🚀 HYBRID AUTOCOMPLETE WITH FREE TEXT */}
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Primary Color
          </Typography>
          {/* Removed excessive debug logging for performance */}
          {isEditable ? (
            <Autocomplete
              key={`primaryColor-${Array.isArray(looksMetadata?.primaryColors)
                ? looksMetadata.primaryColors[0] || 'default'
                : 'default'}`} // STABLE KEY: Only changes with AI data
              freeSolo
              size="small"
              value={editableData.primaryColor}
              inputValue={editableData.primaryColor}
              onChange={(event, newValue) => {
                handleFieldChange('primaryColor', newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                handleFieldChange('primaryColor', newInputValue);
              }}
              options={primaryColors}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Primary Color"
                  placeholder="Type or select a color"
                  helperText="Select from list or type your own color"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    {/* Show AI chip for AI-generated values */}
                    {option === (Array.isArray(looksMetadata?.primaryColors)
                      ? looksMetadata.primaryColors[0]
                      : '') && (
                      <Chip
                        size="small"
                        label="AI"
                        sx={{
                          height: 16,
                          fontSize: '0.7rem',
                          bgcolor: 'success.light',
                          color: 'success.contrastText',
                        }}
                      />
                    )}
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </Box>
                </li>
              )}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {Array.isArray(looksMetadata.primaryColors)
                ? looksMetadata.primaryColors[0] || 'Not specified'
                : looksMetadata.primaryColor || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* Color Scheme - 🚀 CONVERTED TO AUTOCOMPLETE FOR AUTO-POPULATION */}
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Color Scheme
          </Typography>
          {isEditable ? (
            <Autocomplete
              key={`colorScheme-${looksMetadata?.colorScheme || 'default'}`} // STABLE KEY: Only changes with AI data
              freeSolo
              size="small"
              value={editableData.colorScheme}
              inputValue={editableData.colorScheme} // CRITICAL FIX: Explicit inputValue binding
              onChange={(event, newValue) => {
                handleFieldChange('colorScheme', newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                handleFieldChange('colorScheme', newInputValue);
              }}
              options={colorSchemes}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Color Scheme"
                  placeholder="Type or select color scheme"
                  helperText="Select from list or type your own scheme"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    {/* Show AI chip for AI-generated values */}
                    {option.toLowerCase() ===
                      (typeof colorSchemeForOptions === 'string' ? colorSchemeForOptions : '').toLowerCase() && (
                      <Chip
                        size="small"
                        label="AI"
                        sx={{
                          height: 16,
                          fontSize: '0.7rem',
                          bgcolor: 'success.light',
                          color: 'success.contrastText',
                        }}
                      />
                    )}
                    {option.charAt(0).toUpperCase() +
                      option.slice(1).replace('-', ' ')}
                  </Box>
                </li>
              )}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {colorSchemeForOptions || editableData.colorScheme || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* Primary Garment - 🚀 CONVERTED TO AUTOCOMPLETE FOR AUTO-POPULATION */}
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Primary Garment
          </Typography>
          {isEditable ? (
            <Autocomplete
              key={`primaryGarment-${
                looksMetadata?.primaryGarment || 'default'
              }`} // STABLE KEY: Only changes with AI data
              freeSolo
              size="small"
              value={editableData.primaryGarment}
              inputValue={editableData.primaryGarment} // CRITICAL FIX: Explicit inputValue binding
              onChange={(event, newValue) => {
                handleFieldChange('primaryGarment', newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                handleFieldChange('primaryGarment', newInputValue);
              }}
              options={primaryGarments}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Primary Garment"
                  placeholder="Type or select garment type"
                  helperText="Select from list or type your own garment"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    {/* Show AI chip for AI-generated values */}
                    {option.toLowerCase() ===
                      looksMetadata?.primaryGarment?.toLowerCase() && (
                      <Chip
                        size="small"
                        label="AI"
                        sx={{
                          height: 16,
                          fontSize: '0.7rem',
                          bgcolor: 'success.light',
                          color: 'success.contrastText',
                        }}
                      />
                    )}
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </Box>
                </li>
              )}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {looksMetadata.primaryGarment || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* Seasonality - 🚀 CONVERTED TO AUTOCOMPLETE FOR AUTO-POPULATION */}
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Seasonality
          </Typography>
          {isEditable ? (
            <Autocomplete
              key={`seasonality-${looksMetadata?.seasonality || 'default'}`} // STABLE KEY: Only changes with AI data
              freeSolo
              size="small"
              value={editableData.seasonality}
              inputValue={editableData.seasonality} // CRITICAL FIX: Explicit inputValue binding
              onChange={(event, newValue) => {
                handleFieldChange('seasonality', newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                handleFieldChange('seasonality', newInputValue);
              }}
              options={seasonalities}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Seasonality"
                  placeholder="Type or select seasonality"
                  helperText="Select from list or type your own season"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    {/* Show AI chip for AI-generated values */}
                    {option.toLowerCase() ===
                      looksMetadata?.seasonality?.toLowerCase() && (
                      <Chip
                        size="small"
                        label="AI"
                        sx={{
                          height: 16,
                          fontSize: '0.7rem',
                          bgcolor: 'success.light',
                          color: 'success.contrastText',
                        }}
                      />
                    )}
                    {option.charAt(0).toUpperCase() +
                      option.slice(1).replace('-', ' ')}
                  </Box>
                </li>
              )}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {looksMetadata.seasonality || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* Occasion - 🚀 CONVERTED TO AUTOCOMPLETE FOR AUTO-POPULATION */}
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Occasion
          </Typography>
          {isEditable ? (
            <Autocomplete
              key={`occasion-${looksMetadata?.occasion || 'default'}`} // STABLE KEY: Only changes with AI data
              freeSolo
              size="small"
              value={editableData.occasion}
              inputValue={editableData.occasion} // CRITICAL FIX: Explicit inputValue binding
              onChange={(event, newValue) => {
                handleFieldChange('occasion', newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                handleFieldChange('occasion', newInputValue);
              }}
              options={occasions}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Occasion"
                  placeholder="Type or select occasion"
                  helperText="Select from list or type your own occasion"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    {/* Show AI chip for AI-generated values */}
                    {option.toLowerCase() ===
                      (typeof occasionForOptions === 'string' ? occasionForOptions : '').toLowerCase() && (
                      <Chip
                        size="small"
                        label="AI"
                        sx={{
                          height: 16,
                          fontSize: '0.7rem',
                          bgcolor: 'success.light',
                          color: 'success.contrastText',
                        }}
                      />
                    )}
                    {option.charAt(0).toUpperCase() +
                      option.slice(1).replace('-', ' ')}
                  </Box>
                </li>
              )}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {occasionForOptions || editableData.occasion || 'Not specified'}
            </Typography>
          )}
        </Grid>

        {/* Accessories */}
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: 600 }}
          >
            Accessories
          </Typography>
          {isEditable ? (
            <TextField
              fullWidth
              size="small"
              value={editableData.accessories}
              onChange={e => handleFieldChange('accessories', e.target.value)}
              placeholder="e.g., Hat, Jewelry, Bag"
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                p: 1,
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {Array.isArray(looksMetadata.accessories)
                ? looksMetadata.accessories.join(', ')
                : looksMetadata.accessories || 'None'}
            </Typography>
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default LooksInformation;
